import React, { useEffect, useState } from "react";
import { Paper, Box, Switch, Typography } from "@mui/material";
import { UserData } from "../../types/User";
import { getUserListData } from "../../api/UsersApi";
import { getDoctorIdsData } from "../../api/EmployeeApi";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import {
  createData,
  deleteData,
  getData,
  mapDoctorsToAssignedRooms,
} from "../../api/RoomDoctorApi";
import { DoctorAssignedRooms, RoomDoctorData } from "../../types/RoomDoctor";
import { toast } from "react-toastify";

interface ComponentProps {
  consultingRoomId: number;
}

const DoctorList: React.FC<ComponentProps> = ({ consultingRoomId }) => {
  const [listData, setListData] = useState<UserData[]>([]);
  const [roomDoctors, setRoomDoctors] = useState<RoomDoctorData[]>([]);
  const [assignedDoctors, setAssignedDoctors] = useState<DoctorAssignedRooms[]>(
    []
  );
  const [loading, setLoading] = useState(true);

  const handleLoadData = async () => {
    setLoading(true);

    const ids = await getDoctorIdsData();

    const result = await getUserListData(ids);

    setListData(result || []);

    const rooms = await getData();
    setRoomDoctors(rooms);

    setLoading(false);
  };

  const handleToggle = async (
    userId: number,
    consultingRoomId: number,
    assigned: boolean
  ) => {
    setAssignedDoctors((prev) =>
      prev.map((doctor) =>
        doctor.userId === userId ? { ...doctor, isAssigned: assigned } : doctor
      )
    );

    if (assigned) {
      const result = await createData(userId, consultingRoomId);
      if (result == null) {
        toast.error("Failed to assign doctor");
      }

      toast.success("Doctor successfully assigned.");
    } else {
      const result = await deleteData(userId, consultingRoomId);
      if (result == null) {
        toast.error("Failed to remove doctor");
      }

      toast.success("Doctor successfully removed.");
    }
  };

  useEffect(() => {
    handleLoadData();
  }, []);

  useEffect(() => {
    if (listData.length && roomDoctors.length) {
      const result = mapDoctorsToAssignedRooms(
        listData,
        roomDoctors,
        consultingRoomId
      );
      setAssignedDoctors(result);
    }
  }, [listData, roomDoctors]);

  return (
    <div>
      {loading && <LoadingSpinner />}
      {!loading && (
        <Paper elevation={1} sx={{ p: 2 }}>
          {assignedDoctors
            .filter(
              (d) =>
                !roomDoctors.some(
                  (rd) =>
                    rd.userId === d.userId &&
                    rd.consultingRoomId !== consultingRoomId
                )
            )
            .map((d, idx) => {
              return (
                <Box
                  key={d.id}
                  display="flex"
                  alignItems="center"
                  sx={{
                    py: 1,
                    borderBottom:
                      idx < listData.length - 1 ? "1px solid #e0e0e0" : "none",
                  }}
                >
                  {/* Full name column (left) */}
                  <Box flex="1 1 0" minWidth={0}>
                    <Typography variant="body1" noWrap>
                      {d.fullName}
                    </Typography>
                  </Box>

                  {/* Switch column (right) */}
                  <Box
                    flex="0 0 auto"
                    display="flex"
                    alignItems="center"
                    ml={2}
                  >
                    <Switch
                      checked={d.isAssigned}
                      onChange={() =>
                        handleToggle(
                          d.userId,
                          d.consultingRoomId,
                          !d.isAssigned
                        )
                      }
                      color="success"
                      inputProps={{ "aria-label": `assign-doctor-${d.id}` }}
                    />
                  </Box>
                </Box>
              );
            })}
        </Paper>
      )}
    </div>
  );
};

export default DoctorList;
